// src/components/AllInventory.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE from '../config';
import Loading from './Loading';
import { useLanguage } from '../LanguageContext';

const Icon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3" width="18" height="4" rx="1" fill="currentColor" />
    <rect x="3" y="10" width="18" height="11" rx="1" fill="currentColor" />
  </svg>
);

const AllInventory = () => {
  const { t, isRtl } = useLanguage();

  const [rawMaterials, setRawMaterials] = useState([]);
  const [products, setProducts] = useState([]);
  const [uniqueRaw, setUniqueRaw] = useState([]);
  const [uniqueProducts, setUniqueProducts] = useState([]);
  const [receivedPackaging, setReceivedPackaging] = useState([]);
  const [uniquePackaging, setUniquePackaging] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('uniqueRaw'); // 'uniqueRaw', 'uniqueProducts', 'uniquePackaging', 'raw', 'products', 'packaging'
  const [statusFilter, setStatusFilter] = useState('All');

  const formatDate = d => d ? new Date(d).toLocaleDateString(isRtl ? 'ar-EG' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const [rawRes, prodRes, approvedRes, receivedPKRes, masterPKRes] = await Promise.all([
        axios.get(`${API_BASE}/rawmaterials`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/products`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/approved-raw-materials`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/received-packaging`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/packagingmaterials`, { headers: getAuthHeaders() })
      ]);

      const approvedMap = {};
      (approvedRes.data || []).forEach(am => {
        approvedMap[am.name] = am.minQuantityThreshold || 0;
      });

      const filteredRaw = (rawRes.data || []);
      const filteredProd = (prodRes.data || []);

      setRawMaterials(filteredRaw);
      setProducts(filteredProd);

      // Aggregate Unique Raw Materials
      const rawMap = {};
      filteredRaw.forEach(r => {
        const name = r.material_name;
        if (!rawMap[name]) rawMap[name] = 0;
        rawMap[name] += (r.available_quantity || 0);
      });
      const uniqueRawList = (approvedRes.data || []).map(am => {
        const total = rawMap[am.name] || 0;
        const threshold = am.minQuantityThreshold || 0;
        let status = 'Abundant';
        if (total === 0) status = 'Out Of Stock';
        else if (total < threshold) status = 'Less';
        return { name: am.name, total, status };
      });
      setUniqueRaw(uniqueRawList);

      // Aggregate Unique Products
      const prodMap = {};
      filteredProd.forEach(p => {
        const name = p.product_name;
        if (!prodMap[name]) prodMap[name] = 0;
        prodMap[name] += (p.product_quantiry || 0);
      });
      setUniqueProducts(Object.keys(prodMap).map(name => ({ name, total: prodMap[name] })));

      // Aggregate Unique Packaging
      const pkMap = {};
      const filteredPK = (receivedPKRes.data || []);
      setReceivedPackaging(filteredPK);
      filteredPK.forEach(pk => {
        const materialId = pk.packagingMaterialId?._id;
        if (!materialId) return;
        if (!pkMap[materialId]) pkMap[materialId] = 0;
        pkMap[materialId] += (pk.quantity || 0);
      });
      const uniquePKList = (masterPKRes.data || []).map(m => {
        const total = pkMap[m._id] || 0;
        const threshold = m.minimumQuantityRequired || 0;
        let status = 'Abundant';
        if (total === 0) status = 'Out Of Stock';
        else if (total < threshold) status = 'Less';
        return { name: m.name, total, status };
      });
      setUniquePackaging(uniquePKList);
    } catch (err) {
      console.error('Failed to fetch inventory', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventory();
  }, []);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', paddingBottom: 60, direction: isRtl ? 'rtl' : 'ltr' }}>
      {/* Page Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 20, flexWrap: 'wrap', gap: 16
      }}>
        <h1 style={{
          margin: 0, fontSize: 28, fontWeight: 700, color: '#1a1c1d',
          textAlign: isRtl ? 'right' : 'left'
        }}>{t.all_inventory}</h1>
      </div>

      {/* Main Content Card */}
      <div style={{
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
        border: '1px solid #ebebeb',
        overflow: 'hidden'
      }}>
        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #f1f1f1',
          padding: '0 16px',
          background: '#fff',
          flexWrap: 'wrap'
        }}>
          <div
            onClick={() => setActiveTab('uniqueRaw')}
            style={{
              padding: '12px 16px',
              fontSize: 13,
              fontWeight: activeTab === 'uniqueRaw' ? 600 : 500,
              color: activeTab === 'uniqueRaw' ? '#1a1c1d' : '#6d7175',
              cursor: 'pointer',
              borderBottom: activeTab === 'uniqueRaw' ? '3px solid #1976d2' : '3px solid transparent',
              marginBottom: -1,
              transition: 'all 0.2s'
            }}
          >
            {t.unique_raw} ({uniqueRaw.length.toLocaleString(isRtl ? 'ar-EG' : undefined)})
          </div>
          <div
            onClick={() => setActiveTab('uniqueProducts')}
            style={{
              padding: '12px 16px',
              fontSize: 13,
              fontWeight: activeTab === 'uniqueProducts' ? 600 : 500,
              color: activeTab === 'uniqueProducts' ? '#1a1c1d' : '#6d7175',
              cursor: 'pointer',
              borderBottom: activeTab === 'uniqueProducts' ? '3px solid #1976d2' : '3px solid transparent',
              marginBottom: -1,
              transition: 'all 0.2s'
            }}
          >
            {t.unique_products} ({uniqueProducts.length.toLocaleString(isRtl ? 'ar-EG' : undefined)})
          </div>
          <div
            onClick={() => setActiveTab('raw')}
            style={{
              padding: '12px 16px',
              fontSize: 13,
              fontWeight: activeTab === 'raw' ? 600 : 500,
              color: activeTab === 'raw' ? '#1a1c1d' : '#6d7175',
              cursor: 'pointer',
              borderBottom: activeTab === 'raw' ? '3px solid #1976d2' : '3px solid transparent',
              marginBottom: -1,
              transition: 'all 0.2s'
            }}
          >
            {t.raw_materials} ({rawMaterials.length.toLocaleString(isRtl ? 'ar-EG' : undefined)})
          </div>
          <div
            onClick={() => setActiveTab('products')}
            style={{
              padding: '12px 16px',
              fontSize: 13,
              fontWeight: activeTab === 'products' ? 600 : 500,
              color: activeTab === 'products' ? '#1a1c1d' : '#6d7175',
              cursor: 'pointer',
              borderBottom: activeTab === 'products' ? '3px solid #1976d2' : '3px solid transparent',
              marginBottom: -1,
              transition: 'all 0.2s'
            }}
          >
            {t.products} ({products.length.toLocaleString(isRtl ? 'ar-EG' : undefined)})
          </div>
          <div
            onClick={() => setActiveTab('uniquePackaging')}
            style={{
              padding: '12px 16px',
              fontSize: 13,
              fontWeight: activeTab === 'uniquePackaging' ? 600 : 500,
              color: activeTab === 'uniquePackaging' ? '#1a1c1d' : '#6d7175',
              cursor: 'pointer',
              borderBottom: activeTab === 'uniquePackaging' ? '3px solid #1976d2' : '3px solid transparent',
              marginBottom: -1,
              transition: 'all 0.2s'
            }}
          >
            {t.unique_packing} ({uniquePackaging.length.toLocaleString(isRtl ? 'ar-EG' : undefined)})
          </div>
          <div
            onClick={() => setActiveTab('packaging')}
            style={{
              padding: '12px 16px',
              fontSize: 13,
              fontWeight: activeTab === 'packaging' ? 600 : 500,
              color: activeTab === 'packaging' ? '#1a1c1d' : '#6d7175',
              cursor: 'pointer',
              borderBottom: activeTab === 'packaging' ? '3px solid #1976d2' : '3px solid transparent',
              marginBottom: -1,
              transition: 'all 0.2s'
            }}
          >
            {t.all_packing} ({receivedPackaging.length.toLocaleString(isRtl ? 'ar-EG' : undefined)})
          </div>
        </div>

        {/* Filter Bar (Conditional) */}
        {(activeTab === 'uniqueRaw' || activeTab === 'uniquePackaging') && (
          <div style={{ padding: '12px 16px', background: '#f9fafb', borderBottom: '1px solid #f1f1f1', display: 'flex', justifyContent: 'flex-start', flexWrap: 'wrap' }}>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: '1px solid #d2d2d2',
                fontSize: 13,
                color: '#1a1c1d',
                background: '#fff',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="All">{t.all_statuses}</option>
              <option value="Abundant">{t.abundant}</option>
              <option value="Less">{t.less}</option>
              <option value="Out Of Stock">{t.out_of_stock}</option>
            </select>
          </div>
        )}

        {loading ? (
          <Loading message={t.syncing_inventory} />
        ) : (
          <div key={activeTab} className="fade-in" style={{ overflowX: 'auto' }}>
            {activeTab === 'raw' ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f9fafb', height: 40, borderBottom: '1px solid #ebebeb' }}>
                    <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.material_name}</th>
                    <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.batch_no}</th>
                    <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.available_qty}</th>
                  </tr>
                </thead>
                <tbody>
                  {rawMaterials.length === 0 ? (
                    <tr>
                      <td colSpan={3} style={{ padding: 48, textAlign: 'center', color: '#6d7175' }}>{t.no_raw_stock}</td>
                    </tr>
                  ) : (
                    rawMaterials.map((r) => (
                      <tr key={r._id} style={{ borderBottom: '1px solid #f1f1f1' }}>
                        <td style={{ padding: '12px 16px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{r.material_name}</td>
                        <td style={{ padding: '12px 16px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{r.batch_number || '-'}</td>
                        <td style={{ padding: '12px 16px', textAlign: isRtl ? 'left' : 'right' }}>{Number(r.available_quantity || 0).toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 })} {t.kg}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            ) : activeTab === 'products' ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f9fafb', height: 40, borderBottom: '1px solid #ebebeb' }}>
                    <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.product_name}</th>
                    <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.batch_no}</th>
                    <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.mfg_date}</th>
                    <th style={{ textAlign: isRtl ? 'left' : 'right', padding: '10px 16px', fontWeight: 600 }}>{t.quantity}</th>
                  </tr>
                </thead>
                <tbody>
                  {products.length === 0 ? (
                    <tr>
                      <td colSpan={4} style={{ padding: 48, textAlign: 'center', color: '#6d7175' }}>{t.no_products_stock}</td>
                    </tr>
                  ) : (
                    products.map((p) => (
                      <tr key={p._id} style={{ borderBottom: '1px solid #f1f1f1' }}>
                        <td style={{ padding: '12px 16px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{p.product_name}</td>
                        <td style={{ padding: '12px 16px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{p.batch || '-'}</td>
                        <td style={{ padding: '12px 16px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{formatDate(p.manufacture_date)}</td>
                        <td style={{ padding: '12px 16px', textAlign: isRtl ? 'left' : 'right' }}>{Number(p.product_quantiry || 0).toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 })} {t.kg}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            ) : activeTab === 'uniqueRaw' ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f9fafb', height: 40, borderBottom: '1px solid #ebebeb' }}>
                    <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.material_name}</th>
                    <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.status}</th>
                    <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.total_quantity}</th>
                  </tr>
                </thead>
                <tbody>
                  {uniqueRaw.length === 0 ? (
                    <tr>
                      <td colSpan={3} style={{ padding: 48, textAlign: 'center', color: '#6d7175' }}>{t.no_raw_found}</td>
                    </tr>
                  ) : (
                    uniqueRaw
                      .filter(r => statusFilter === 'All' || r.status === statusFilter)
                      .map((r, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #f1f1f1' }}>
                          <td style={{ padding: '12px 16px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{r.name}</td>
                          <td style={{ padding: '12px 16px', textAlign: isRtl ? 'right' : 'left' }}>
                            {r.status === 'Out Of Stock'
                              ? <span style={{ background: '#fff1f0', color: '#cf1322', padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 500 }}>{t.out_of_stock}</span>
                              : r.status === 'Less'
                                ? <span style={{ background: '#fffbe6', color: '#d48806', padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 500 }}>{t.less}</span>
                                : <span style={{ background: '#f6ffed', color: '#389e0d', padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 500 }}>{t.abundant}</span>
                            }
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: isRtl ? 'left' : 'right' }}>{Number(r.total || 0).toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 })} {t.kg}</td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            ) : activeTab === 'packaging' ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f9fafb', height: 40, borderBottom: '1px solid #ebebeb' }}>
                    <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.material_name}</th>
                    <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.supplier}</th>
                    <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.arrival_date}</th>
                    <th style={{ textAlign: isRtl ? 'left' : 'right', padding: '10px 16px', fontWeight: 600 }}>{t.received_qty}</th>
                    <th style={{ textAlign: isRtl ? 'left' : 'right', padding: '10px 16px', fontWeight: 600 }}>{t.available_qty}</th>
                  </tr>
                </thead>
                <tbody>
                  {receivedPackaging.length === 0 ? (
                    <tr><td colSpan={5} style={{ padding: 48, textAlign: 'center', color: '#6d7175' }}>{t.no_packing_stock}</td></tr>
                  ) : (
                    receivedPackaging.map(r => (
                      <tr key={r._id} style={{ borderBottom: '1px solid #f1f1f1' }}>
                        <td style={{ padding: '12px 16px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{r.packagingMaterialId?.name}</td>
                        <td style={{ padding: '12px 16px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{r.supplierId?.supplier_name || '-'}</td>
                        <td style={{ padding: '12px 16px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{formatDate(r.receivingDate)}</td>
                        <td style={{ padding: '12px 16px', textAlign: isRtl ? 'left' : 'right', color: '#6d7175' }}>{Number(r.received_quantity || r.quantity || 0).toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 })}</td>
                        <td style={{ padding: '12px 16px', textAlign: isRtl ? 'left' : 'right', fontWeight: 600 }}>{Number(r.quantity || 0).toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 })}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            ) : activeTab === 'uniquePackaging' ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f9fafb', height: 40, borderBottom: '1px solid #ebebeb' }}>
                    <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.material_name}</th>
                    <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.status}</th>
                    <th style={{ textAlign: isRtl ? 'left' : 'right', padding: '10px 16px', fontWeight: 600 }}>{t.total_quantity}</th>
                  </tr>
                </thead>
                <tbody>
                  {uniquePackaging.length === 0 ? (
                    <tr><td colSpan={3} style={{ padding: 48, textAlign: 'center', color: '#6d7175' }}>{t.no_packing_found}</td></tr>
                  ) : (
                    uniquePackaging
                      .filter(r => statusFilter === 'All' || r.status.toLowerCase() === statusFilter.toLowerCase())
                      .map((r, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #f1f1f1' }}>
                          <td style={{ padding: '12px 16px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{r.name}</td>
                          <td style={{ padding: '12px 16px', textAlign: isRtl ? 'right' : 'left' }}>
                            {r.status === 'Out Of Stock'
                              ? <span style={{ background: '#fff1f0', color: '#cf1322', padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 500 }}>{t.out_of_stock}</span>
                              : r.status === 'Less'
                                ? <span style={{ background: '#fffbe6', color: '#d48806', padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 500 }}>{t.less}</span>
                                : <span style={{ background: '#f6ffed', color: '#389e0d', padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 500 }}>{t.abundant}</span>
                            }
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: isRtl ? 'left' : 'right' }}>{Number(r.total || 0).toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 })}</td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f9fafb', height: 40, borderBottom: '1px solid #ebebeb' }}>
                    <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.product_name}</th>
                    <th style={{ textAlign: isRtl ? 'left' : 'right', padding: '10px 16px', fontWeight: 600 }}>{t.total_quantity}</th>
                  </tr>
                </thead>
                <tbody>
                  {uniqueProducts.length === 0 ? (
                    <tr>
                      <td colSpan={2} style={{ padding: 48, textAlign: 'center', color: '#6d7175' }}>{t.no_products_found}</td>
                    </tr>
                  ) : (
                    uniqueProducts.map((p, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #f1f1f1' }}>
                        <td style={{ padding: '12px 16px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{p.name}</td>
                        <td style={{ padding: '12px 16px', textAlign: isRtl ? 'left' : 'right' }}>{Number(p.total || 0).toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 })} {t.kg}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AllInventory;